import React, { useCallback, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { useTranslation } from "react-i18next";

import qs from "query-string";

import { useApolloClient, useMutation } from "@apollo/client";
import { MUTATION_CONFIRM_EMAIL } from "../../config/graphql/mutation";

import { useDomainCheck } from "../../lib/hooks/useDomainCheck";

import DefaultEmailVerification from "./DefaultEmailVerification";
import CenterParcsEmailVerification from "./CenterParcsEmailVerification";

export type EmailVerificationStatus =
  | "confirm"
  | "success"
  | "alreadyConfirmed"
  | "error";

// Confirms on the button click only: mail scanners open the link before the
// person does and used to burn the code.
const EmailVerification = React.memo(() => {
  const location = useLocation();
  const history = useHistory();

  const { t } = useTranslation(["emailVerification"]);

  const client = useApolloClient();

  const { isCenterParcsDomain } = useDomainCheck();

  const [mutateAsync, { called, loading, error, data }] = useMutation<{
    confirmEmail: {
      alreadyConfirmed: boolean;
      token?: string | null;
      resetPasswordCode?: string | null;
    };
  }>(MUTATION_CONFIRM_EMAIL);

  const status = useMemo((): EmailVerificationStatus => {
    if (error) {
      return "error";
    }

    if (data) {
      return data.confirmEmail.alreadyConfirmed
        ? "alreadyConfirmed"
        : "success";
    }

    return "confirm";
  }, [error, data]);

  const query = useMemo(
    () =>
      qs.parse(location.search, {
        // A hex code like "12e4…" would parse as a number.
        parseNumbers: false,
        parseBooleans: true,
      }) as {
        code?: string;
        email?: string;
        update?: boolean;
      },
    [location.search],
  );

  useEffect(() => {
    if (!query.code) {
      history.push("/login");
    }
  }, [query.code, history]);

  const onSubmit = useCallback(() => {
    const token = data?.confirmEmail.token;

    if (status === "success" && token) {
      window.localStorage.setItem("Authorization", token);

      client.refetchQueries({
        updateCache: (cache) => {
          cache.evict({ fieldName: "token" });
        },
        include: ["QUERY_AUTH"],
      });

      history.replace("/");

      return;
    }

    if (status !== "confirm") {
      history.replace("/login");

      return;
    }

    if (called || !query.code) {
      return;
    }

    mutateAsync({
      variables: {
        input: {
          code: query.code,
          update: query.update,
        },
      },
    }).catch(() => null);
  }, [
    called,
    client,
    data,
    history,
    mutateAsync,
    query.code,
    query.update,
    status,
  ]);

  // Only the confirming call carries a code; a repeat open uses the reset mail.
  const onSetOwnPassword = useMemo(() => {
    const code = data?.confirmEmail.resetPasswordCode;

    if (status === "success" && code) {
      return () => history.replace(`/reset-password?${qs.stringify({ code })}`);
    }

    if (status === "alreadyConfirmed") {
      return () => history.replace("/forgot-password");
    }

    return undefined;
  }, [data, history, status]);

  const emailVerificationProps = {
    status,
    disabled: loading,
    onSubmit,
    onSetOwnPassword,
    t,
  };

  if (isCenterParcsDomain) {
    return <CenterParcsEmailVerification {...emailVerificationProps} />;
  }

  return <DefaultEmailVerification {...emailVerificationProps} />;
});

export default EmailVerification;
